'use client'

import { CheckCircleIcon, ClockIcon, UserGroupIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { useTranslations } from '@/lib/next-shim'

import { GetConsultationQuery } from '@/core/graphql/types'

import { useWaitingRoom } from '../model/useWaitingRoom'

interface PatientWaitingRoomProps {
  consultationId: string
  initialConsultation: GetConsultationQuery['consultation']
}

export function PatientWaitingRoom({ consultationId, initialConsultation }: PatientWaitingRoomProps) {
  const t = useTranslations()
  const { consultation, isReady, isJoining, handleJoin } = useWaitingRoom(consultationId, initialConsultation)

  const doctorName = consultation.medecin
    ? `Dr. ${consultation.medecin.userInfo.name} ${consultation.medecin.userInfo.surname}`
    : t('PatientWaitingRoom.yourDoctor')

  return (
    <div className="flex min-h-screen w-full flex-1 flex-col items-center justify-center bg-gray-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-primary p-6 text-white text-center">
          <h1 className="text-2xl font-bold">{t('PatientWaitingRoom.title')}</h1>
          <p className="opacity-90 mt-1">{consultation.speciality || t('PatientWaitingRoom.consultation')}</p>
        </div>

        <div className="p-8 space-y-8">
          {/* Status Section */}
          <div className="text-center space-y-4">
            {isReady ? (
              <div className="animate-in fade-in zoom-in duration-500">
                <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircleIcon className="w-12 h-12 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">{t('PatientWaitingRoom.doctorReady')}</h2>
                <p className="text-gray-500">{t('PatientWaitingRoom.canJoinNow')}</p>
              </div>
            ) : (
              <div>
                <div className="mx-auto w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-4 relative">
                  <ClockIcon className="w-12 h-12 text-blue-500 animate-pulse" />
                  <div className="absolute inset-0 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <h2 className="text-xl font-bold text-gray-900">{t('PatientWaitingRoom.waitingForDoctor')}</h2>
                <p className="text-gray-500">{doctorName}</p>
              </div>
            )}
          </div>

          {/* Info Cards */}
          {!isReady && (
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center space-x-4">
                <UserGroupIcon className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">{t('PatientWaitingRoom.position')}</p>
                  <p className="text-lg font-bold text-blue-900">{t('PatientWaitingRoom.calculating')}</p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-4">
            {isReady ? (
              <button
                onClick={handleJoin}
                disabled={isJoining}
                className="w-full py-4 px-6 bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl shadow-lg shadow-green-200 transition-all transform hover:-translate-y-1 active:translate-y-0 disabled:opacity-50"
              >
                {isJoining ? t('common.loading') : t('PatientWaitingRoom.joinButton')}
              </button>
            ) : (
              <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 flex items-start space-x-3">
                <InformationCircleIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-yellow-800">{t('PatientWaitingRoom.autoRedirectNote')}</p>
              </div>
            )}
          </div>

          {/* Footer / Tech Check */}
          <div className="pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">{t('PatientWaitingRoom.techReminder')}</p>
          </div>
        </div>
      </div>
    </div>
  )
}
